import { useCallback, useEffect } from "react";
import applicantDetailsServices from "../../../../services/api/service/applicantDetailsService";
import { setStatusPopup } from "../../../../services/redux/slices/statusPopupSlice";
import { removeDigiLockerLoaderId } from "../../../../services/redux/slices/stepperDynamicFormSlice";

export function useDigiLocker({
  dispatch,
  navigate,
  locationSearch,
  tabIndex,
  applicants,
  aadhaarApplicationNo,
  formUserData,
  documentApplicantType,
  submissionTrackingId,
  setAggregatorLoader,
  refreshDocuments,
}: {
  dispatch: any;
  navigate: any;
  locationSearch: string;
  tabIndex: number;
  applicants: string[];
  aadhaarApplicationNo: any;
  formUserData: any;
  documentApplicantType: string;
  submissionTrackingId: any;
  setAggregatorLoader: (next: boolean) => void;
  refreshDocuments: () => void;
}) {
  const handleGetIssuedFiles = useCallback(
    async (transactionId: string) => {
      await applicantDetailsServices
        .applicantIssueFile(transactionId)
        .then((response: any) => {
          refreshDocuments();
          dispatch(
            setStatusPopup({
              status: "success",
              message: response?.data?.message,
            })
          );
        })
        .catch((err: any) => {
          dispatch(
            setStatusPopup({
              status: "error",
              message: err?.response?.data?.message,
            })
          );
        });
    },
    [dispatch, refreshDocuments]
  );

  const handleVerifyViaDigiLocker = useCallback(async () => {
    try {
      setAggregatorLoader(true);
      const payload = {
        documentId: aadhaarApplicationNo,
        employmentCategory:
          tabIndex === 0
            ? formUserData?.employmentType || formUserData?.applicantType
            : formUserData?.[`employmentCoApplicant${applicants?.[tabIndex]?.split(" ")?.[1]}employmentType`] ||
              formUserData?.applicantType,
        typeOfApplicant: documentApplicantType,
        formSubmissionTrackingId: submissionTrackingId,
      };

      const response = await applicantDetailsServices.verifyAadhaarApplication(
        payload
      );

      if (response?.status === 200) {
        const currentSearch = locationSearch || "";
        const newSearch = currentSearch.replace(/[?&]document_verified=true/g, "");
        const cleanSearch = newSearch.replace(/^\?&/, "?").replace(/^&/, "?");
        const newUrl = `${window.location.pathname}${cleanSearch}`;
        navigate(newUrl, { replace: true });

        if (response?.data?.data?.uploadedDocuments.length === 0) {
          const transactionId = response?.data?.data?.transactionId;

          handleGetIssuedFiles(transactionId);
          setTimeout(() => handleGetIssuedFiles(transactionId), 2000);
          setTimeout(() => handleGetIssuedFiles(transactionId), 4000);

          setTimeout(() => {
            setAggregatorLoader(false);
            dispatch(removeDigiLockerLoaderId());
          }, 6000);

          setTimeout(() => {
            refreshDocuments();
          }, 7000);

          return;
        } else {
          setAggregatorLoader(false);
        }

        dispatch(removeDigiLockerLoaderId());
        dispatch(
          setStatusPopup({
            status: "success",
            message: response?.data?.message,
          })
        );
        refreshDocuments();
      }
    } catch (error: any) {
      dispatch(removeDigiLockerLoaderId());
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message,
        })
      );
    }
  }, [
    setAggregatorLoader,
    tabIndex,
    applicants,
    aadhaarApplicationNo,
    formUserData,
    documentApplicantType,
    submissionTrackingId,
    locationSearch,
    navigate,
    handleGetIssuedFiles,
    dispatch,
    refreshDocuments,
  ]);

  useEffect(() => {
    let searchString = locationSearch || "";
    if (searchString.includes("?")) {
      const parts = searchString.split("?");
      if (parts.length > 2) {
        searchString = parts[0] + "?" + parts.slice(1).join("&");
      }
    }

    const params = new URLSearchParams(searchString);
    const documentVerified = params.get("document_verified");
    if (documentVerified === "true" && tabIndex !== -1) {
      handleVerifyViaDigiLocker();
    }
  }, [locationSearch, tabIndex, handleVerifyViaDigiLocker]);

  return {
    handleVerifyViaDigiLocker,
  };
}

